// ── 출퇴근 편집 모달 훅 ──────────────────────────────────────────────────────
// AttendanceManagement에서 분리 (300줄 룰). 편집 모달 상태 + openEdit/saveEdit/patchTimeForm.
//   동작은 분리 전과 100% 동일 (순수 추출). useAttendanceData/useTimeSettings 와 같은 패턴.
import { useState, useMemo } from 'react';
import { apiClient } from '../../../api/apiClient';
import { toDateString } from '../../../utils/date';
import {
  getTotalMinutesByHHmm, getAutoBreakMinutes, getRowAutoBreakMinutes,
  getRowTotalMinutes, isValidHHmm, toLocalTimestampString, normalizeIp,
} from './utils';
import { formatTimeTyping } from '../../../utils/timeInput';

/**
 * 출퇴근 편집 모달 상태 + 핸들러
 * @param {object} params
 * @param {string}   params.attendancePath - 출퇴근 데이터 API 경로
 * @param {Array}    params.ips - IP 목록 (등록 IP 참고용)
 * @param {Function} params.showToast - 토스트 표시 함수
 * @param {Function} params.fetchAll - 저장 후 목록 재조회
 */
export function useAttendanceEdit({ attendancePath, ips, showToast, fetchAll }) {
  const [editing,             setEditing]             = useState(null);
  const [breakManuallyEdited, setBreakManuallyEdited] = useState(false);
  const [form, setForm] = useState({
    date: '', check_in_time: '', ip_address: '', check_in_address: '',
    check_out_time: '', check_out_ip_address: '', check_out_address: '', break_minutes: '',
  });

  // 편집 중인 직원의 등록 IP + 통합(user_id NULL) IP 목록 (참고용) — 2026-04-29
  const editingRegisteredIps = useMemo(() => {
    if (!editing) return [];
    return ips.filter((ip) => ip.is_active && (
      ip.user_id === null || ip.user_id === undefined
        || Number(ip.user_id) === Number(editing.user_id)
    ));
  }, [ips, editing]);

  // ── 편집 열기 ─────────────────────────────────────────────────────────────
  async function openEdit(row) {
    let latest = row;
    const attendanceId = Number(row?.id);
    if (Number.isInteger(attendanceId) && attendanceId > 0) {
      const result = await apiClient.get(`${attendancePath}/${attendanceId}`);
      if (!result.ok) {
        showToast(result.data?.message || '최신 출퇴근 기록을 불러오지 못했습니다.', 'error');
        return;
      }
      latest = result.data?.row || row;
    }

    const totalMinutes    = getRowTotalMinutes(latest);
    const autoBreakNumber = getRowAutoBreakMinutes(latest, totalMinutes);
    const autoBreak       = autoBreakNumber === null ? '' : String(autoBreakNumber);
    const initialBreak    = (latest.break_minutes === null || latest.break_minutes === undefined || latest.break_minutes === '')
      ? autoBreak : String(latest.break_minutes);

    setEditing(latest);
    setBreakManuallyEdited(false);
    setForm({
      date:                 toDateString(latest.date_ymd || latest.date || ''),
      check_in_time:        String(latest.check_in_hhmm || ''),
      ip_address:           normalizeIp(latest.ip_address),
      check_in_address:     latest.check_in_address || '',
      check_out_time:       String(latest.check_out_hhmm || ''),
      check_out_ip_address: normalizeIp(latest.check_out_ip_address),
      check_out_address:    latest.check_out_address || '',
      break_minutes:        initialBreak,
    });
  }

  // 시간 필드 변경 시 자동 휴게시간 재계산
  function patchTimeForm(field, value) {
    // 모바일 숫자 키패드에 ':'가 없어 숫자만 쳐도 HH:mm이 되게 실시간 포맷 ("0959" → "09:59")
    const formatted = formatTimeTyping(value);
    setForm((prev) => {
      const next = { ...prev, [field]: formatted };
      if (!breakManuallyEdited) {
        const totalMinutes = getTotalMinutesByHHmm(next.check_in_time, next.check_out_time);
        next.break_minutes = totalMinutes === null ? '' : String(getAutoBreakMinutes(totalMinutes));
      }
      return next;
    });
  }

  // ── 편집 저장 ─────────────────────────────────────────────────────────────
  async function saveEdit() {
    if (!editing) return;
    if (!form.date) { showToast('날짜를 선택해 주세요.', 'error'); return; }

    const totalMinutesByForm = getTotalMinutesByHHmm(form.check_in_time, form.check_out_time);
    const autoBreakByForm    = totalMinutesByForm === null ? null : getAutoBreakMinutes(totalMinutesByForm);
    const breakMinutes       = form.break_minutes === '' ? autoBreakByForm : Number(form.break_minutes);

    if (form.break_minutes !== '' && (!Number.isFinite(breakMinutes) || breakMinutes < 0 || breakMinutes > 600)) {
      showToast('휴게시간은 0~600분으로 입력해 주세요.', 'error'); return;
    }
    if (form.check_in_time  && !isValidHHmm(form.check_in_time))  { showToast('출근 시간은 24시간 형식(HH:mm)으로 입력해 주세요. 예: 09:30', 'error'); return; }
    if (form.check_out_time && !isValidHHmm(form.check_out_time)) { showToast('퇴근 시간은 24시간 형식(HH:mm)으로 입력해 주세요. 예: 18:10', 'error'); return; }

    const payload = {
      date:                form.date || null,
      check_in:            toLocalTimestampString(form.date, form.check_in_time),
      check_out:           toLocalTimestampString(form.date, form.check_out_time),
      ip_address:          form.ip_address || null,
      check_in_address:    form.check_in_address || null,
      check_out_ip_address: form.check_out_ip_address || null,
      check_out_address:   form.check_out_address || null,
      break_minutes:       breakMinutes,
    };

    const result = await apiClient.put(`${attendancePath}/${editing.id}`, payload);
    if (!result.ok) { showToast(result.data?.message || '수정 실패', 'error'); return; }

    showToast('출퇴근 정보가 수정되었습니다.', 'success');
    setEditing(null);
    fetchAll();
  }

  return {
    editing, setEditing, form, setForm, patchTimeForm,
    breakManuallyEdited, setBreakManuallyEdited, editingRegisteredIps,
    openEdit, saveEdit,
  };
}
